import React from 'react';
import { Outcome, SessionResult } from '../types/problem';
import { plural } from '../utils/format';
import Icon from './Icon';

interface ResultsProps {
  results: SessionResult[];
  onDone: () => void;
}

const OUTCOME_BADGE: Record<Outcome, { label: string; tone: string }> = {
  recalled: { label: 'Recalled', tone: 'success' },
  missed: { label: 'Missed · back tomorrow', tone: 'danger' },
  skipped: { label: 'Skipped', tone: 'neutral' },
};

export default function Results({ results, onDone }: ResultsProps) {
  const count = (o: Outcome) => results.filter(r => r.outcome === o).length;
  const recalled = count('recalled');
  const missed = count('missed');
  const skipped = count('skipped');
  const graded = recalled + missed;
  const accuracy = graded ? Math.round((recalled / graded) * 100) : 0;

  const r = 30;
  const circumference = 2 * Math.PI * r;

  return (
    <div className="page-narrow">
      <div className="results-head">
        <svg className="results-ring" viewBox="0 0 72 72" role="img" aria-label={`${accuracy}% recalled`}>
          <circle className="track" cx="36" cy="36" r={r} />
          <circle
            className="value"
            cx="36"
            cy="36"
            r={r}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - accuracy / 100)}
            transform="rotate(-90 36 36)"
          />
          <text x="36" y="36">{graded ? `${accuracy}%` : '—'}</text>
        </svg>
        <h1 className="page-title">Session complete</h1>
        <p className="page-subtitle">
          {graded
            ? `You recalled ${recalled} of ${plural(graded, 'problem')}. Missed ones come back tomorrow.`
            : 'No problems were graded this session.'}
        </p>
      </div>

      <div className="stats stats-3">
        <div className="card stat">
          <div className="stat-label">Recalled</div>
          <div className="stat-value tone-success">{recalled}</div>
        </div>
        <div className="card stat">
          <div className="stat-label">Missed</div>
          <div className="stat-value tone-danger">{missed}</div>
        </div>
        <div className="card stat">
          <div className="stat-label">Skipped</div>
          <div className="stat-value">{skipped}</div>
        </div>
      </div>

      {results.length > 0 && (
        <section className="card">
          <div className="card-header"><div className="card-title">Problems</div></div>
          <ul className="list">
            {results.map(({ problem, outcome }) => (
              <li key={problem.id}>
                <a className="list-row" href={problem.url} target="_blank" rel="noopener noreferrer">
                  <div className="list-main"><div className="list-title">{problem.title}</div></div>
                  <span className={`badge badge-${OUTCOME_BADGE[outcome].tone}`}>{OUTCOME_BADGE[outcome].label}</span>
                  <span className="list-icon"><Icon name="external" size={14} /></span>
                </a>
              </li>
            ))}
          </ul>
          <div className="card-footer">
            <button className="btn btn-primary" onClick={onDone}>Back to dashboard</button>
          </div>
        </section>
      )}
    </div>
  );
}
